/**
 * Surveys — the ONE website normaliser (docs/SURVEYS.md "Public branding").
 * Used by the API (the public GET applies it to the effective website —
 * the survey's own `website_url` override, else the org's
 * `organization_business_identity.website_url` — as defence in depth), by the
 * create / update request schemas (a survey override that does not normalise
 * is a 400) and by the web builder (inline error + the preview's button).
 */
/** The `surveys.website_url` cap (the DB CHECK is `char_length <= 2048`). */
export const SURVEY_WEBSITE_URL_MAX = 2_048;
/** A host that looks like a domain (`acme.nl`, `www.acme.co.uk`) — what a scheme-less website gets `https://` for. */
const HOSTNAME_LIKE_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+(?::\d{1,5})?(?:[/?#]\S*)?$/i;
/** Starts with a URL scheme (`https:`, `javascript:`, `localhost:`, `user:`) — never given a default scheme. */
const SCHEME_PREFIX_RE = /^[a-z][a-z0-9+.-]*:/i;
/** A dotted-quad — what WHATWG URL parsing normalises every IPv4 spelling (`1.2`, `0x7f.1`) to. */
const IPV4_HOST_RE = /^\d{1,3}(?:\.\d{1,3}){3}$/;
/**
 * The ONE website normaliser: a free-text website → an absolute `http:` /
 * `https:` URL, or `null` (no button). A scheme-less value gets `https://` —
 * an ASCII hostname-looking one (`acme.nl`, `acme.nl:8080/x`) or any other
 * value that does not start with a scheme or a slash (so a unicode host
 * `café.fr` behaves like `https://café.fr`). Then it must parse with an
 * http(s) protocol (`javascript:`, `data:`, `ftp:` and garbage never reach the
 * page) and name a PUBLIC-looking host: no credentials (`user:pass@`, the
 * `https://acme.nl@evil.com` disguise), no IP literal (v4 in any spelling, v6
 * `[…]`) and no single-label host (`localhost`, `intranet`).
 */
export function normalizeWebsiteUrl(raw) {
    const value = (raw ?? "").trim();
    if (!value)
        return null;
    const schemeless = HOSTNAME_LIKE_RE.test(value) || (!SCHEME_PREFIX_RE.test(value) && !value.startsWith("/") && !value.startsWith("\\"));
    try {
        const url = new URL(schemeless ? `https://${value}` : value);
        if (url.protocol !== "http:" && url.protocol !== "https:")
            return null;
        if (url.username || url.password)
            return null;
        const host = url.hostname.replace(/\.$/, "");
        if (!host || host.startsWith("[") || IPV4_HOST_RE.test(host) || !host.includes("."))
            return null;
        return url.href;
    }
    catch {
        return null;
    }
}
/** The `surveys.website_label` cap (the DB CHECK is `char_length <= 60`). */
export const SURVEY_WEBSITE_LABEL_MAX = 60;
/**
 * The ONE rule for the website button, used by the public page and the
 * builder preview alike. `url` is the effective website (raw or normalised —
 * it is normalised here); a URL that does not normalise → `null` (no button,
 * whatever the label). `isCustom` = the survey has its own link (not the
 * org's). A non-blank `label` always wins.
 */
export function surveyWebsiteButton(input) {
    const href = normalizeWebsiteUrl(input.url);
    if (!href)
        return null;
    const label = input.label?.trim();
    if (label)
        return { href, kind: "custom", text: label };
    if (input.isCustom)
        return { href, kind: "host", text: new URL(href).hostname };
    return { href, kind: "org", text: input.orgName };
}
//# sourceMappingURL=surveysUrl.js.map